import { HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { take } from 'rxjs/operators';
import { DocketService } from 'src/app/api/core/docket/docket.service';
import { BffService } from 'src/app/api/utils/comms/bff/bff.service';
import { WatcherService } from 'src/app/api/utils/watcher/watcher.service';
import { FoodDrinkType, Order } from 'src/app/models/order.model';
import { CommsService } from '../../../../utils/comms/comms.service';
import { POLL_UPDATE } from '../../../../utils/watcher/watcher-event';
import { AuthService } from '../../../auth/auth.service';
import { CoreService } from '../../../core/core.service';
import { HandleFailedDispatchService } from '../handle.failed.dispatch.service';
import { BffDispatchRequest, OrderDispatcherService } from '../order-dispatcher/order-dispatcher.service';
import { FeatureFlagList, FeatureFlagsService } from './../../../settings/feature-flags.service';

@Injectable({
  providedIn: 'root',
})
export class DispatchSmsService extends OrderDispatcherService {
  constructor(
    public watcher: WatcherService,
    public comms: CommsService,
    public auth: AuthService,
    public core: CoreService,
    public docketService: DocketService,
    public handleFailedDispatchService: HandleFailedDispatchService,
    public featureFlagsService: FeatureFlagsService,
    public bffService: BffService,
  ) {
    super(core, docketService, auth, bffService);
  }

  /**
   * Simplified function to send food or drink SMS
   * @param orderId - The order ID to send SMS for
   * @param time - The time to set when the SMS is sent
   * @param type - The type of SMS: 'food' or 'drink'
   */
  async sendFoodOrDrinkSMS(orderId: string, time: string, type: FoodDrinkType): Promise<any> {
    return new Promise(resolve => {
      const order: Order = this.docketService.getOrderById(orderId);
      if (!order) {
        resolve(false);
        return;
      }

      // Store the original value before setting the temporary value
      const originalValue = type === 'food' ? order.collect_at_counter_sms_food : order.collect_at_counter_sms_drink;

      // Set the appropriate SMS field based on type (temporary value until API confirms)
      if (type === 'food') {
        order.collect_at_counter_sms_food = time;
      } else if (type === 'drink') {
        order.collect_at_counter_sms_drink = time;
      }

      // Make API call
      this.updateOrder_Drupal(order, type)
        .pipe(take(1))
        .subscribe(
          payload => {
            if (!this.auth.Logged_in) {
              resolve(false);
              return;
            }

            if (payload && payload.success) {
              this._updateFoodOrDrinkSMS(order, payload, type);
              resolve(true);
            } else {
              // Pass the type and original value so we can restore it if it was previously set
              this.handleFailedDispatchService.handleFailedUpdateRequest(order, 'sms', null, {
                ...payload,
                smsType: type,
                originalValue,
              });
              resolve(false);
            }
          },
          fail => {
            // Pass the type and original value so we can restore it if it was previously set
            this.handleFailedDispatchService.handleFailedUpdateRequest(order, 'sms', null, {
              smsType: type,
              originalValue,
            });
            resolve(false);
          },
        );
    });
  }

  /**
   * Send a request to the server to send an SMS to customer when order is ready
   * @param orderId - The order of the id to be updated
   * @param time - The time to set when the SMS is sent
   */
  async sendDocketSMS(orderId: string, time: string): Promise<any> {
    return new Promise(resolve => {
      let order: Order = this.docketService.getOrderById(orderId);
      if (!order) {
        // order not found
        resolve(false);
        return;
      }

      order.collect_at_counter_sms = time;

      let sendSMSResponse: Observable<any> = this.updateOrder_Drupal(order);

      // Separate BFF Call
      if (this.featureFlagsService.isFeatureFlagEnabled(FeatureFlagList.BFF)) {
        this.dispatchDocket([orderId], BffDispatchRequest.SMS).subscribe(
          bffPayload => {
            console.log('BFF SMS Success:', bffPayload);
            // trackevents
          },
          bffFailed => {
            console.log('BFF SMS Failed:', bffFailed);
            // trackevents
          },
        );
      }

      sendSMSResponse.pipe(take(1)).subscribe(
        payload => {
          // block propagation of response if user already logged out
          if (!this.auth.Logged_in) {
            resolve(false);
            return;
          }

          if (payload && payload.success) {
            this._updateOrderSMS(order, payload);
            resolve(true);
          } else {
            this.handleFailedDispatchService.handleFailedUpdateRequest(order, 'sms');
            resolve(false);
          }
        },
        fail => {
          this.handleFailedDispatchService.handleFailedUpdateRequest(order, 'sms');
          resolve(false);
        },
      );
    });
  }

  /**
   * Update the active orders with new SMS
   * @param orders - The raw/json orders
   * @param payload - returned json from service
   */
  private _updateOrderSMS(order: Order, payload: any): void {
    //set time on raw order
    order.collect_at_counter_sms = payload.collect_at_counter_sms;

    // update the collect_at_counter_sms property
    this.docketService.updateOrder(order, ['collect_at_counter_sms']);
    // fire if there are changes on the dockets
    this.watcher.broadcast(POLL_UPDATE);
  }

  /**
   * Update the active orders with new food or drink SMS
   * @param order - The order to update
   * @param payload - returned json from service
   * @param type - The type of SMS: 'food' or 'drink'
   */
  private _updateFoodOrDrinkSMS(order: Order, payload: any, type: FoodDrinkType): void {
    // TODO: Backend is returning payload payload.collect_at_counter_sms
    // we should update this to use the _food or _drink.

    // Use the timestamp from the server response (source of truth)
    if (type === 'food') {
      order.collect_at_counter_sms_food = payload.collect_at_counter_sms_food;
      console.log('docket time:', order.collect_at_counter_sms_food);
    } else if (type === 'drink') {
      order.collect_at_counter_sms_drink = payload.collect_at_counter_sms_drink;
      console.log('docket time:', order.collect_at_counter_sms_drink);
    }

    // Update the order in the service
    const updateFields = type === 'food' ? ['collect_at_counter_sms_food'] : ['collect_at_counter_sms_drink'];
    this.docketService.updateOrder(order, updateFields);
    // Fire if there are changes on the dockets
    this.watcher.broadcast(POLL_UPDATE);
  }

  updateOrder_Drupal(sendObj: any, type?: FoodDrinkType): Observable<any> {
    return new Observable(subscriber => {
      const header = new HttpHeaders(this.auth.headerData);
      // force 30 seconds timeout for send bump status
      let url = this.core.Active_server.url + 'api/bump/send-instruction-sms/' + sendObj.id + '/' + sendObj.location_id;
      if (type) {
        url += '/' + type;
      }
      this.comms.get(
        url,
        {
          headers: header,
        },
        payload => {
          subscriber.next(payload);
          subscriber.complete();
        },
        err => {
          subscriber.error(err);
          subscriber.complete();
        },
        this.UPDATE_TIMEOUT,
      );
    });
  }
}
